import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { celebrateDailyTasks } from "../utils/daily_celebration"
import { showSystemToast } from "../utils/system_toast"
import { mentionChipFor } from "../utils/mention_chips"
import { hostTaskPane, taskPaneTrigger } from "../utils/task_pane"

// Board (kanban) view for a task list. Columns are the list's top-level
// (root) tasks; cards are their direct children. Same dependency-free HTML5
// drag pattern as tasks_outline_controller and pipeline_board_controller:
// dragging a card onto a column reparents it under that column's root task,
// dropping it before/after a card in the column reorders it too. Both persist
// via POST /tasks/:id/move.json (parent_id = target root id, after_id/
// before_id from the drop position) with an optimistic DOM move that reverts
// on failure. Dragging column headers to reorder root tasks is not
// implemented this phase — card drag is the must-have.
export default class extends Controller {
    static targets = ["column", "card"]
    static values = { list: String }

    connect() {
        this._dragging = null
        this._origin = null
        this._clientId = (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
            ? crypto.randomUUID()
            : `board-${Date.now()}-${Math.random().toString(36).slice(2)}`
        // The task pane opens in this board's sidebar (detail-modal); a task
        // it completes repaints here, and its counts follow the card.
        this._unhostPane = hostTaskPane(this, {
            onCompleted: (id, completed) => {
                const card = this._card(id)
                if (card) this._setCompleted(card, completed)
            }
        })
        this._onCommentsCount = (event) => this._renderCount(event.detail, "comments")
        this._onAttachmentsCount = (event) => this._renderCount(event.detail, "files")
        window.addEventListener("jane:task-comments-count", this._onCommentsCount)
        window.addEventListener("jane:task-attachments-count", this._onAttachmentsCount)
    }

    disconnect() {
        this._unhostPane?.()
        window.removeEventListener("jane:task-comments-count", this._onCommentsCount)
        window.removeEventListener("jane:task-attachments-count", this._onAttachmentsCount)
    }

    _card(id) {
        return this.element.querySelector(`[data-board-card][data-task-id="${CSS.escape(String(id))}"]`)
    }

    // A comment or file added in the pane updates the card's existing count.
    // A card that had none gets its badge on the next load.
    _renderCount({ taskId, count } = {}, section) {
        const badge = this._card(taskId)?.querySelector(`[data-pane-section="${section}"] span`)
        if (badge) badge.textContent = String(count)
    }

    // A card click opens the task pane in this board's sidebar, at the section
    // of the badge clicked (data-pane-section) - except when the click is the
    // complete toggle. Cmd/ctrl-click opens the task in the list (data-href).
    onCardClick(e) {
        if (e.target.closest("[data-board-card-check]")) return
        // A person or company chip in the title opens its card in the side
        // panel (detail-modal, on the board element) instead of the task.
        const chip = mentionChipFor(e)
        if (chip) {
            e.preventDefault()
            e.stopPropagation()
            this.dispatch("open-detail", { detail: { trigger: chip } })
            return
        }
        // A link in the title opens itself; the card must not also open.
        if (e.target.closest("a[href]")) return
        const card = e.target.closest("[data-board-card]")
        if (!card) return
        // Cmd/ctrl-click still takes you to the task in the list.
        if ((e.metaKey || e.ctrlKey) && card.dataset.href) {
            window.open(card.dataset.href, "_blank", "noopener")
            return
        }
        const section = e.target.closest("[data-pane-section]")?.dataset.paneSection || "details"
        this.dispatch("open-detail", { detail: { trigger: taskPaneTrigger(card.dataset.taskId, section) } })
    }

    toggleComplete(e) {
        e.stopPropagation()
        const card = e.target.closest("[data-board-card]")
        // A section heading card has no checkbox and is never completed.
        if (!card || card.dataset.textStyle) return
        const wasCompleted = card.dataset.completed === "true"
        const url = `/tasks/${card.dataset.taskId}/${wasCompleted ? "uncomplete" : "complete"}.json`
        this._setCompleted(card, !wasCompleted)
        this._post(url, new FormData()).then((data) => {
            if (!data || !data.ok) {
                this._setCompleted(card, wasCompleted)
                return
            }
            if (!wasCompleted) celebrateDailyTasks(data.celebration)
            // A repeating task's next occurrence is a new card the server made;
            // say so rather than leave it invisible until a reload.
            if (!wasCompleted && data.spawned) {
                const due = data.spawned.due_date ? new Date(`${data.spawned.due_date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : null
                showSystemToast(due ? `Done - the next one is on ${due}` : "Done - the next one is on the list", { level: "success", duration: 3000 })
            }
        })
    }

    _setCompleted(card, completed) {
        card.dataset.completed = completed ? "true" : "false"
        card.classList.toggle("is-completed", completed)
        // A finished card stops counting toward its column's open points.
        this._recount()
    }

    // --- Drag lifecycle ---

    onDragStart(e) {
        const card = e.target.closest("[data-board-card]")
        if (!card) return
        this._dragging = card
        this._origin = { column: card.closest("[data-board-column]"), next: card.nextElementSibling }
        card.classList.add("is-dragging")
        e.dataTransfer.effectAllowed = "move"
        try { e.dataTransfer.setData("text/plain", card.dataset.taskId) } catch (_) { /* ignore */ }
    }

    onDragEnd() {
        if (this._dragging) this._dragging.classList.remove("is-dragging")
        this.columnTargets.forEach((c) => c.classList.remove("is-drop-target"))
        this._clearCardDropTargets()
        this._dragging = null
        this._origin = null
    }

    onDragOver(e) {
        if (!this._dragging) return
        const column = e.target.closest("[data-board-column]")
        if (!column) return
        e.preventDefault()
        e.dataTransfer.dropEffect = "move"
        this.columnTargets.forEach((c) => c.classList.toggle("is-drop-target", c === column))

        this._clearCardDropTargets()
        const overCard = e.target.closest("[data-board-card]")
        if (overCard && overCard !== this._dragging) {
            overCard.classList.add(this._dropPosition(overCard, e.clientY) === "before" ? "is-drop-before" : "is-drop-after")
        }
    }

    onDrop(e) {
        if (!this._dragging) return
        const column = e.target.closest("[data-board-column]")
        if (!column) return
        e.preventDefault()

        const card = this._dragging
        const fromColumn = this._origin.column
        const overCard = e.target.closest("[data-board-card]")
        this.columnTargets.forEach((c) => c.classList.remove("is-drop-target"))
        this._clearCardDropTargets()

        if (overCard === card) { this.onDragEnd(); return }

        if (overCard) {
            if (this._dropPosition(overCard, e.clientY) === "before") overCard.before(card)
            else overCard.after(card)
        } else {
            const body = column.querySelector("[data-board-column-body]") || column
            body.appendChild(card)
        }
        this._recount()

        const rootId = column.dataset.rootId
        const afterId = card.previousElementSibling ? card.previousElementSibling.dataset.taskId : null
        const beforeId = afterId ? null : (card.nextElementSibling ? card.nextElementSibling.dataset.taskId : null)

        this._move(card.dataset.taskId, rootId, afterId, beforeId).then((ok) => {
            if (!ok) {
                const originBody = fromColumn.querySelector("[data-board-column-body]") || fromColumn
                if (this._origin.next && this._origin.next.parentNode === originBody) {
                    originBody.insertBefore(card, this._origin.next)
                } else {
                    originBody.appendChild(card)
                }
                this._recount()
                this._flash("Could not move the task — put it back.")
            }
            this._dragging = null
            this._origin = null
        })
    }

    // Which side of the target card the pointer is over, mirroring
    // tasks_outline_controller's _dropPosition so first-position drops work.
    _dropPosition(card, clientY) {
        const rect = card.getBoundingClientRect()
        return clientY < rect.top + rect.height / 2 ? "before" : "after"
    }

    _clearCardDropTargets() {
        this.cardTargets.forEach((c) => c.classList.remove("is-drop-before", "is-drop-after"))
    }

    // Keep each column's header count in sync with its card total.
    _recount() {
        this.columnTargets.forEach((column) => {
            const count = column.querySelectorAll("[data-board-card]").length
            const label = column.querySelector("[data-board-column-count]")
            if (label) label.textContent = count
            // Open points per numeric field (tasks/_board), summed from the cards.
            column.querySelectorAll("[data-board-column-points]").forEach((slot) => {
                const fieldId = slot.dataset.boardColumnPoints
                let total = 0
                column.querySelectorAll("[data-board-card]").forEach((card) => {
                    if (card.dataset.completed === "true") return
                    let values = {}
                    try { values = JSON.parse(card.dataset.fieldValues || "{}") || {} } catch { values = {} }
                    total += Number(values[fieldId]) || 0
                })
                const out = slot.querySelector("[data-board-column-points-total]")
                if (out) out.textContent = String(Math.round(total * 100) / 100)
                slot.hidden = total === 0
            })
        })
    }

    _move(taskId, rootId, afterId, beforeId) {
        const fd = new FormData()
        fd.set("parent_id", rootId)
        if (afterId) fd.set("after_id", afterId)
        else if (beforeId) fd.set("before_id", beforeId)
        return this._post(`/tasks/${taskId}/move.json`, fd).then((data) => Boolean(data && data.ok))
    }

    _post(url, formData) {
        return fetch(this._withList(url), {
            method: "POST",
            headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json", "X-Task-List-Client-Id": this._clientId },
            body: formData,
            credentials: "same-origin"
        }).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    }

    // The server resolves the target task list from params[:list], so every
    // request must carry it — otherwise a move on a non-default list would
    // silently hit the default list instead. Mirrors tasks_outline_controller.
    _withList(url) {
        if (!this.hasListValue || !this.listValue) return url
        return `${url}${url.includes("?") ? "&" : "?"}list=${encodeURIComponent(this.listValue)}`
    }

    _flash(message) {
        if (typeof window !== "undefined" && window.alert) window.alert(message)
    }
}
